import { FC, useState, createContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCustomSettings } from 'src/Common/api/companySettingsApi';

/** Stored API values; legacy "Touch" is treated as Terminal3 in the UI. */
export type DefaultPOSTerminalValue = 'Simple' | 'Terminal3';

const PUBLIC_ROUTE_PREFIXES = ['/login', '/register', '/register-store', '/forgot-password', '/reset-password', '/qr'];

export function normalizeDefaultPOSTerminal(raw: string | null | undefined): DefaultPOSTerminalValue {
  if (raw === 'Terminal3' || raw === 'Simple') return raw;
  if (raw === 'Touch') return 'Terminal3';
  return 'Simple';
}

type SidebarContext = {
  sidebarToggle: any;
  toggleSidebar: () => void;
  closeSidebar: () => void;
  defaultPOSTerminal: DefaultPOSTerminalValue;
  updatePOSTerminal: (terminal: DefaultPOSTerminalValue) => void;
  storeType: string | null;
  isRestaurantOrCafe: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SidebarContext = createContext<SidebarContext>(
  {} as SidebarContext
);

export const SidebarProvider: FC = ({ children }) => {
  const location = useLocation();
  const [sidebarToggle, setSidebarToggle] = useState(false);
  const [defaultPOSTerminal, setDefaultPOSTerminal] = useState<DefaultPOSTerminalValue>('Simple');
  const [storeType, setStoreType] = useState<string | null>(null);

  // Resolve store type from local/session profile (unchanged)
  useEffect(() => {
    const resolveStoreType = (): string | null => {
      const candidates = [
        localStorage.getItem('storeType'),
        localStorage.getItem('StoreType'),
        localStorage.getItem('store_type')
      ];
      for (const val of candidates) {
        if (val && val.trim().length > 0) return val.trim();
      }

      try {
        const profileDataRaw = sessionStorage.getItem('profileData');
        if (profileDataRaw) {
          const parsed = JSON.parse(profileDataRaw);
          const first = Array.isArray(parsed) ? parsed[0] : parsed;
          const pdType = first?.StoreType || first?.storeType;
          if (typeof pdType === 'string' && pdType.trim().length > 0) return pdType.trim();
        }
      } catch {
        /* ignore */
      }

      try {
        const employeeProfileRaw = localStorage.getItem('employeeProfile');
        if (employeeProfileRaw) {
          const emp = JSON.parse(employeeProfileRaw);
          const empType = emp?.StoreType || emp?.storeType;
          if (typeof empType === 'string' && empType.trim().length > 0) return empType.trim();
        }
      } catch {
        /* ignore */
      }

      return null;
    };

    const initialStoreType = resolveStoreType();
    if (initialStoreType) {
      setStoreType(initialStoreType);
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'storeType' || e.key === 'StoreType' || e.key === 'store_type' || e.key === 'employeeProfile') {
        setStoreType(e.newValue);
      }
    };

    const handleStoreTypeChange = (e: CustomEvent<{ storeType: string }>) => {
      setStoreType(e.detail.storeType);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('storeTypeChanged' as any, handleStoreTypeChange as any);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storeTypeChanged' as any, handleStoreTypeChange as any);
    };
  }, []);

  // Default POS terminal: always from API when authenticated (not localStorage)
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const path = location.pathname;
    if (PUBLIC_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await getCustomSettings();
        if (cancelled) return;
        const row = res?.customSettings?.find((s: { settingKey: string }) => s.settingKey === 'DefaultPOSTerminal');
        setDefaultPOSTerminal(normalizeDefaultPOSTerminal(row?.settingValue));
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    const handlePOSTerminalChange = (e: CustomEvent<{ terminal: string }>) => {
      setDefaultPOSTerminal(normalizeDefaultPOSTerminal(e.detail?.terminal));
    };

    window.addEventListener('posTerminalChanged' as any, handlePOSTerminalChange as any);
    return () => {
      window.removeEventListener('posTerminalChanged' as any, handlePOSTerminalChange as any);
    };
  }, []);

  const toggleSidebar = () => {
    setSidebarToggle(!sidebarToggle);
  };

  const closeSidebar = () => {
    setSidebarToggle(false);
  };

  const updatePOSTerminal = (terminal: DefaultPOSTerminalValue) => {
    setDefaultPOSTerminal(terminal);
    window.dispatchEvent(
      new CustomEvent('posTerminalChanged', {
        detail: { terminal }
      })
    );
  };

  const isRestaurantOrCafe = (() => {
    if (!storeType) return false;
    const normalized = storeType.toLowerCase().trim();
    const cafeVariants = ['cafe', 'cafy'];
    const restaurantVariants = ['restaurant', 'Resturant'];
    return cafeVariants.includes(normalized) || restaurantVariants.includes(normalized);
  })();

  return (
    <SidebarContext.Provider
      value={{
        sidebarToggle,
        toggleSidebar,
        closeSidebar,
        defaultPOSTerminal,
        updatePOSTerminal,
        storeType,
        isRestaurantOrCafe
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
